<script setup lang="ts">
import { GameCard } from '@/components/game/game-card/index.js';
import {
    applyHandLayoutFromCenter,
    applyStackedLayout,
    initializeCards,
    type LayoutOptions,
} from '@/components/game/player-hand/hand-layout.js';
import { cn } from '@pokerly/ui';
import { useTemplateRefsList } from '@vueuse/core';
import { nextTick, onMounted, useTemplateRef, watch } from 'vue';

interface Props {
    values?: (number | string)[] | readonly (string | number)[];
    collapsed?: boolean;
    debug?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
    values: () => [],
    collapsed: false,
});
const model = defineModel();

const cardEls = useTemplateRefsList<HTMLDivElement>();
const cardContainerEl = useTemplateRef('cardContainer');
const layoutOptions: LayoutOptions = {
    overlapPx: 16,
    curvePx: 15,
    rotateDeg: 20,
    liftCenter: 0,
    startOrigin: 'bottomCenter',
    animate: true,
};

async function relayout() {
    await nextTick();
    const layout = () => {
        if (cardContainerEl.value === null) return;
        if (props.collapsed) {
            applyStackedLayout(cardContainerEl.value, cardEls.value);
            return;
        }
        applyHandLayoutFromCenter(cardContainerEl.value, cardEls.value, layoutOptions);
    };
    layout();
    requestAnimationFrame(() => layout());
}

onMounted(async () => {
    if (!cardContainerEl.value) return;
    initializeCards(cardContainerEl.value, cardEls.value, layoutOptions);
    await relayout();
});

watch(
    () => props.values,
    () => relayout(),
    { deep: true, flush: 'post' },
);

watch(
    () => props.collapsed,
    () => relayout(),
    { flush: 'post' },
);

function onCardClick(value: number | string) {
    model.value = model.value === value ? null : value;
}
</script>

<template>
    <div
        data-slot="player-hand"
        :data-collapsed="collapsed"
        :class="cn('relative', debug ? 'bg-red-500' : '')"
        ref="cardContainer"
    >
        <div v-for="(value, i) in values" :key="i" :ref="cardEls.set" class="absolute">
            <GameCard
                @click="onCardClick(value)"
                :value="value"
                :selected="model == value"
                :face="collapsed ? 'down' : 'up'"
                :disabled="collapsed"
            />
        </div>
    </div>
</template>
